import axios from 'axios';
import {
  InventoryTransferCompleteResponseSchema,
  InventoryTransferDetailResponseSchema,
  InventoryTransferLineChangeResponseSchema,
  InventoryTransferListResponseSchema,
  InventoryTransferScanResponseSchema,
  InventoryTransferStoreLabelResponseSchema,
  type InventoryTransferCompleteResponse,
  type InventoryTransferDetail,
  type InventoryTransferLineChangeResponse,
  type InventoryTransferListItem,
  type InventoryTransferScanResponse,
  type InventoryTransferStoreLabelResponse,
} from '../schemas/apiResponse.schemas';
import { validateResponse } from './validateResponse';

const API_BASE = '/api/inventory-transfers';

export async function fetchInventoryTransfers(
  includeCompleted = false,
): Promise<InventoryTransferListItem[]> {
  const response = await axios.get(API_BASE, {
    params: includeCompleted ? { includeCompleted: 'true' } : undefined,
  });
  const parsed = validateResponse(
    InventoryTransferListResponseSchema,
    response.data,
    'fetchInventoryTransfers',
  );
  return parsed.requests;
}

export async function fetchInventoryTransfer(id: string): Promise<InventoryTransferDetail> {
  const response = await axios.get(`${API_BASE}/${encodeURIComponent(id)}`);
  const parsed = validateResponse(
    InventoryTransferDetailResponseSchema,
    response.data,
    'fetchInventoryTransfer',
  );
  return parsed.request;
}

export async function recordInventoryTransferScan(
  transferRequestId: string,
  lineId: string,
): Promise<InventoryTransferScanResponse> {
  const response = await axios.post(`${API_BASE}/${encodeURIComponent(transferRequestId)}/scans`, {
    lineId,
  });
  return validateResponse(
    InventoryTransferScanResponseSchema,
    response.data,
    'recordInventoryTransferScan',
  );
}

export async function changeInventoryTransferQuantity(
  transferRequestId: string,
  lineId: string,
  quantity: number,
): Promise<InventoryTransferLineChangeResponse> {
  const response = await axios.post(
    `${API_BASE}/${encodeURIComponent(transferRequestId)}/lines/${encodeURIComponent(lineId)}/quantity`,
    { quantity },
  );
  return validateResponse(
    InventoryTransferLineChangeResponseSchema,
    response.data,
    'changeInventoryTransferQuantity',
  );
}

export async function substituteInventoryTransferSku(
  transferRequestId: string,
  lineId: string,
  sku: string,
  note?: string,
): Promise<InventoryTransferLineChangeResponse> {
  const response = await axios.post(
    `${API_BASE}/${encodeURIComponent(transferRequestId)}/lines/${encodeURIComponent(lineId)}/substitute`,
    note ? { sku, note } : { sku },
  );
  return validateResponse(
    InventoryTransferLineChangeResponseSchema,
    response.data,
    'substituteInventoryTransferSku',
  );
}

export async function printInventoryTransferStoreLabels(
  transferRequestId: string,
  lineId: string,
): Promise<InventoryTransferStoreLabelResponse> {
  const response = await axios.post(
    `${API_BASE}/${encodeURIComponent(transferRequestId)}/lines/${encodeURIComponent(lineId)}/store-labels`,
  );
  return validateResponse(
    InventoryTransferStoreLabelResponseSchema,
    response.data,
    'printInventoryTransferStoreLabels',
  );
}

export async function completeInventoryTransfer(
  transferRequestId: string,
): Promise<InventoryTransferCompleteResponse> {
  const response = await axios.post(`${API_BASE}/${encodeURIComponent(transferRequestId)}/complete`);
  return validateResponse(
    InventoryTransferCompleteResponseSchema,
    response.data,
    'completeInventoryTransfer',
  );
}
